import { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import VotingABI from '../abi/Voting.json';
import { parseContractError } from '../utils/parseError';

const CONTRACT_ADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS;
const SEPOLIA_RPC_URL  = process.env.REACT_APP_SEPOLIA_RPC_URL;

export default function P04VoteManage({ isAdmin, onBackClick, onFinishClick, onVoteClick }) {
  const [tab, setTab]               = useState('A');
  const [candidates, setCandidates] = useState([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [isLoading, setLoading]     = useState(false);
  const [showModal, setShowModal]   = useState(false);
  const [errorMsg, setErrorMsg]     = useState('');
  const pollRef = useRef(null);

  useEffect(() => { if (!isAdmin) onBackClick(); }, [isAdmin, onBackClick]);
  useEffect(() => { initTab(); return () => stopPoll(); }, []);

  async function initTab() {
    try {
      const provider = new ethers.JsonRpcProvider(SEPOLIA_RPC_URL);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, provider);
      const state = await contract.getState();
      if (state === 1n) {
        const [list, total] = await Promise.all([
          contract.getCandidates(), contract.getTotalVotes(),
        ]);
        setCandidates(list.map(c => ({ id: Number(c.id), name: c.name, voteCount: Number(c.voteCount) })));
        setTotalVotes(Number(total)); setTab('B'); startPoll();
      } else {
        const list = await contract.getCandidates();
        setCandidates(list.map(c => ({ id: Number(c.id), name: c.name, voteCount: Number(c.voteCount) })));
      }
    } catch (e) { console.error('초기화 오류:', e); }
  }

  function startPoll() {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const provider = new ethers.JsonRpcProvider(SEPOLIA_RPC_URL);
        const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, provider);
        const [list, total] = await Promise.all([
          contract.getCandidates(), contract.getTotalVotes(),
        ]);
        setCandidates(list.map(c => ({ id: Number(c.id), name: c.name, voteCount: Number(c.voteCount) })));
        setTotalVotes(Number(total));
      } catch (e) { console.error('폴링 오류:', e); }
    }, 5000);
  }

  function stopPoll() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }

  async function handleStartVoting() {
    setLoading(true); setErrorMsg('');
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer   = await provider.getSigner();
      const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, signer);
      const tx = await contract.startVoting();
      await tx.wait();
      const readProv = new ethers.JsonRpcProvider(SEPOLIA_RPC_URL);
      const readC = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, readProv);
      const list = await readC.getCandidates();
      setCandidates(list.map(c => ({ id: Number(c.id), name: c.name, voteCount: Number(c.voteCount) })));
      setTotalVotes(0); setTab('B'); startPoll();
    } catch (err) {
      const msg = parseContractError(err);
      setErrorMsg(msg || '투표 시작 중 오류가 발생했습니다.');
      if (!msg) console.error(err);
    } finally { setLoading(false); }
  }

  async function handleEndVoting() {
    setShowModal(false); setLoading(true); setErrorMsg('');
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer   = await provider.getSigner();
      const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, signer);
      const tx = await contract.endVoting();
      await tx.wait();
      stopPoll(); onFinishClick();
    } catch (err) {
      const msg = parseContractError(err);
      setErrorMsg(msg || '투표 종료 중 오류가 발생했습니다.');
      if (!msg) console.error(err);
    } finally { setLoading(false); }
  }

  const maxVotes = candidates.length > 0 ? Math.max(...candidates.map(c => c.voteCount)) : 0;
  const canStart = candidates.length >= 2;

  if (tab === 'A') {
    return (
      <div className="ev-page" style={{ maxWidth: 680 }}>
        {isLoading && (
          <div className="ev-overlay">
            <div className="ev-spinner" />
            <p className="ev-overlay-text">블록체인에 기록 중...</p>
          </div>
        )}
        <div className="ev-manage-toolbar">
          <button className="ev-btn ev-btn-primary" disabled>▶ 투표 시작</button>
          <button className="ev-btn ev-btn-ghost" disabled>■ 투표 종료</button>
          <div className="ev-manage-state">
            <span className="ev-status-badge ev-status-idle">● 대기 중 (IDLE)</span>
          </div>
        </div>

        {errorMsg && <div className="ev-error">{errorMsg}</div>}

        <div className="ev-card" style={{ marginBottom: 20 }}>
          <div className="ev-card-title">등록된 후보자</div>
          <div className="ev-candidate-list">
            {candidates.map(c => (
              <div key={c.id} className="ev-candidate-row">
                <span className="ev-candidate-row__num">#{c.id}</span>
                <span className="ev-candidate-row__name">{c.name}</span>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 12, fontSize: '0.85rem', color: canStart ? 'var(--green)' : 'var(--text-sub)' }}>
            총 {candidates.length}명 {canStart ? '✓ 투표 시작 가능' : '— 최소 2명 필요'}
          </p>
        </div>

        <div className="ev-info" style={{ marginBottom: 20 }}>
          <p style={{ fontWeight: 700, marginBottom: 6 }}>▶ 투표를 시작하면:</p>
          <p>• 후보자 추가가 불가능해집니다.</p>
          <p>• 세폴리아 지갑을 보유한 누구나 투표할 수 있습니다.</p>
          <p>• 관리자가 수동으로 종료하기 전까지 투표가 유지됩니다.</p>
        </div>

        <div className="ev-btn-group">
          <button className="ev-btn ev-btn-ghost" onClick={onBackClick} disabled={isLoading}>← 뒤로</button>
          <button className="ev-btn ev-btn-primary" onClick={handleStartVoting} disabled={!canStart || isLoading}>
            투표 시작하기 ▶
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ev-page" style={{ maxWidth: 680 }}>
      {isLoading && (
        <div className="ev-overlay">
          <div className="ev-spinner" />
          <p className="ev-overlay-text">블록체인에 기록 중...</p>
        </div>
      )}
      {showModal && (
        <div className="ev-modal-backdrop">
          <div className="ev-modal">
            <h2 className="ev-modal-title">투표 종료 확인</h2>
            <div className="ev-modal-body">
              ⚠ 투표를 종료하시겠습니까?<br />
              종료 후에는 되돌릴 수 없으며 결과가 블록체인에 최종 확정됩니다.
            </div>
            <div className="ev-modal-actions">
              <button className="ev-btn ev-btn-ghost" onClick={() => setShowModal(false)}>취소</button>
              <button className="ev-btn ev-btn-danger" onClick={handleEndVoting}>투표 종료하기 ■</button>
            </div>
          </div>
        </div>
      )}

      <div className="ev-manage-toolbar">
        <button className="ev-btn ev-btn-ghost" disabled>▶ 투표 시작</button>
        <button className="ev-btn ev-btn-danger" onClick={() => setShowModal(true)} disabled={isLoading}>
          ■ 투표 종료
        </button>
        <div className="ev-manage-state">
          <span className="ev-status-badge ev-status-voting">● 투표 진행 중</span>
          <span>총 <strong>{totalVotes}</strong>표</span>
        </div>
      </div>

      {errorMsg && <div className="ev-error">{errorMsg}</div>}

      <div className="ev-card" style={{ marginBottom: 16 }}>
        <div className="ev-card-title">실시간 현황</div>
        <div className="ev-candidate-list">
          {candidates.map(c => {
            const pct = totalVotes > 0 ? ((c.voteCount / totalVotes) * 100).toFixed(1) : '0.0';
            const barW = maxVotes > 0 ? ((c.voteCount / maxVotes) * 100).toFixed(0) : 0;
            const isLead = maxVotes > 0 && c.voteCount === maxVotes;
            return (
              <div key={c.id}>
                <div className="ev-candidate-row">
                  <span className="ev-candidate-row__num">#{c.id}</span>
                  <span className="ev-candidate-row__name">{c.name}</span>
                  {isLead && <span className="ev-leader-chip">▲ 선두</span>}
                  <span className="ev-candidate-row__votes">{c.voteCount}표</span>
                  <span className="ev-candidate-row__pct">{pct}%</span>
                </div>
                <div className="ev-progress-wrap" style={{ margin: '-6px 0 6px' }}>
                  <div className={`ev-progress-bar ${isLead ? 'ev-progress-bar--leader' : ''}`}
                    style={{ width: `${barW}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {onVoteClick && (
        <div className="ev-btn-group">
          <button className="ev-btn ev-btn-ghost" onClick={onVoteClick} disabled={isLoading}>
            🗳️ 투표 참여하기
          </button>
          <button className="ev-btn ev-btn-danger" onClick={() => setShowModal(true)} disabled={isLoading}>
            투표 종료하기 ■
          </button>
        </div>
      )}
      <div className="ev-manage-warning">
        ⚠ 투표 종료 시 결과가 확정됩니다. 되돌릴 수 없습니다.
      </div>
    </div>
  );
}
